import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { isUnauthenticated } from '../../api/call.js';
import { useFreshMe } from '../../api/me.js';
import { Button } from '../../components/Button.js';
import { DangerAlert } from '../../components/DangerAlert.js';
import { PageHeading } from '../../components/PageHeading.js';
import { ChangePasswordCard } from '../../settings/ChangePasswordCard.js';
import { ProfileCard } from '../../settings/ProfileCard.js';
import styles from '../../settings/settings.module.css';

/**
 * The Settings page (`product/domain-glossary.md`, Settings page; user settings
 * `ux.md`): the Account User's own profile details and Change password. Open to
 * every Account User, with no permission (`patterns/auth-patterns.md`, 2).
 *
 * It sits inside the `_app` layout, so the session check and the frame are log
 * in's. The header shows at once; the cards wait for a fresh read of the
 * Account User, so permissions changed since log in show correctly, and a
 * failed read shows no profile or form at all (`technical.md`, "Fresh profile
 * on open").
 */
export const Route = createFileRoute('/_app/settings')({
  component: SettingsPage,
});

function SettingsPage() {
  const me = useFreshMe();
  // A session that ended is already on its way to log in (the one global rule),
  // so it keeps the loading state rather than flashing the load-failed alert.
  const loadFailed = me.isError && !me.isFetching && !isUnauthenticated(me.error);

  return (
    <>
      <PageHeading>Settings</PageHeading>
      <p className={styles.pageCaption}>Your profile and password</p>

      <div className={styles.cards}>
        {me.isSuccess ? (
          <>
            <ProfileCard me={me.data} />
            <ChangePasswordCard />
          </>
        ) : loadFailed ? (
          <DangerAlert
            title="Settings didn't load"
            action={
              <Button
                variant="secondary"
                onClick={() => {
                  void me.refetch();
                }}
              >
                Try again
              </Button>
            }
          >
            Check your connection and try again.
          </DangerAlert>
        ) : (
          <SettingsLoading />
        )}
      </div>
    </>
  );
}

/** Placeholder bars appear only once loading has taken 300ms (`patterns/ui-patterns.md`, 9). */
const PLACEHOLDER_DELAY_MS = 300;

function SettingsLoading() {
  const [showBars, setShowBars] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setShowBars(true), PLACEHOLDER_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div aria-busy="true" className={styles.loading}>
      <p className="visually-hidden" role="status">
        Loading settings
      </p>
      {showBars
        ? [0, 1].map((card) => (
            <div key={card} className={styles.placeholderCard} aria-hidden="true">
              <div className={styles.placeholderBar} />
              <div className={styles.placeholderBar} />
              <div className={styles.placeholderBar} />
            </div>
          ))
        : null}
    </div>
  );
}
